/**
 * O selo da plataforma de envelope no painel de integridade (E98.F7.T02, ADR-0083 §2.1).
 *
 * A plataforma embute um selo digital ao exportar cada PDF. Ele comprova que o arquivo não
 * mudou desde a exportação — e só isso: não é assinatura do signatário, e a data dele é a da
 * exportação, não a do ato. O quadro diz as duas coisas, e diz quando a assinatura foi lida em
 * modo tolerante porque o leitor estrito recusou a estrutura do arquivo.
 */
import { Badge } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { AssinaturaDigitalApuradaData } from '@/lib/exame_eletronico_assinatura_digital';

import { Titulo, formatarInstante } from './QuadroVerificacaoTabelas';

const t = ptBR.exameEletronico.selo;

export function SelosDaPlataforma({
  apurado,
}: {
  apurado: AssinaturaDigitalApuradaData;
}): JSX.Element | null {
  const selos = apurado.selos ?? [];
  if (selos.length === 0 && apurado.leitura_tolerante !== true) return null;
  return (
    <section className="space-y-sc-2" data-testid="quadro-selo-da-plataforma">
      {selos.length > 0 ? <Titulo>{t.titulo}</Titulo> : null}
      {selos.map((selo) => (
        <div key={selo.campo} className="space-y-sc-1" data-testid="quadro-selo">
          <p className="flex flex-wrap items-center gap-sc-2 text-body-sm">
            <Badge variant={selo.integra ? 'success' : 'danger'}>
              {selo.integra ? t.integro : t.naoIntegro}
            </Badge>
            <span className="text-fg">
              {t.exportadoEm.replace('{quando}', formatarInstante(selo.assinado_em, false))}
            </span>
          </p>
          <p className="max-w-prose text-body-sm text-fg-muted">{t.naoEAssinatura}</p>
        </div>
      ))}
      {apurado.leitura_tolerante === true ? (
        <p className="max-w-prose text-caption text-fg-dim" data-testid="quadro-leitura-tolerante">
          {t.leituraTolerante}
        </p>
      ) : null}
    </section>
  );
}
